import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as d3 from 'd3'
import { Tooltip, SegmentedControl } from '@mantine/core'
import { GoQuestion } from 'react-icons/go'
import './charts.css'

type floorsType = {
  label: string
  value: string
}

type dataSetType = {
  date: Date
  value: number
  name: string
}
type propType = {
  dataSet: dataSetType[]
  selectedType: string[]
  setSelectedType: (input: string[]) => void
  floors: floorsType[]
  floorIDs: string[]
  selectedFloor: string
  setSelectedFloor: (inp: string) => void
}

const LineChart = ({
  dataSet,
  selectedType,
  setSelectedType,
  floors,
  floorIDs,
  selectedFloor,
  setSelectedFloor,
}: propType) => {
  const lineChartConatiner = useRef(null)
  const [data, setData] = useState([])

  const dataGroups = useMemo(() => {
    return d3.groups(dataSet, (d) => d.name)
  }, [dataSet])
  const formatTime = d3.timeFormat('%d %b')
  const color = d3
    .scaleOrdinal<string>()
    .domain(dataGroups.map((data) => data[0]))
    .range(['#FF718B', '#93AAFD', '#962DFF'])

  const getFloorName = (floorName) => {
    const name = floors.find(
      (floor) => floor.value === floorName.split('-')[0]
    )?.label

    return name.split('- ')[1]
  }

  const typeDescription = [
    {
      type: 'frequency',
      description:
        'The hours the room was used divided by the number of hours included in the survey',
    },
    {
      type: 'occupancy',
      description:
        'This calculates the number of workplaces used in the room during the hours that it was occupied',
    },
    {
      type: 'utilization',
      description:
        'The utilization rate measures the number of “space hours” used during the week compared to the number of space hours available',
    },
  ]

  useEffect(() => {
    setData(dataGroups)
  }, [dataSet])

  const [svgWidth, setSvgWidth] = useState<number | null>(null)
  useEffect(() => {
    const svg = d3.select(lineChartConatiner.current)
    const getWidth = () => {
      setSvgWidth(svg.node().clientWidth)
    }
    window.addEventListener('resize', getWidth)

    return () => {
      window.removeEventListener('resize', getWidth)
    }
  }, [])

  useEffect(() => {
    const svg = d3.select(lineChartConatiner.current)
    const margin = { top: 50, bottom: 50, left: 50, right: 50 }
    const height = 550
    const width = svgWidth || svg.node().clientWidth

    const w = width - margin.left - margin.right
    const h = height - margin.top - margin.bottom

    svg.selectAll('*').remove()

    svg.attr('width', '100%').attr('height', h + margin.top + margin.bottom)

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(dataSet.map((d) => d.date)))
      .range([0, w])

    const yScale = d3
      .scaleLinear()
      .domain(d3.extent(dataSet.map((d) => d.value)))
      .range([h, 0])

    g.append('g')
      .attr('transform', `translate(0, ${h})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(dataSet.map((d) => d.date))
          .tickFormat((d) => formatTime(d as Date))
          .tickPadding(10)
          .tickSize(6)
          .tickSizeOuter(0)
      )

    g.append('g')
      .attr('class', 'grid')
      .call(
        d3
          .axisLeft(yScale)
          .tickSize(-w)
          .tickFormat(null)
          .tickPadding(20)
          .tickSizeOuter(0)
      )
    // line generator

    const line = d3
      .line<{ date: Date; value: number; name: string }>()
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.value))
      .curve(d3.curveBumpX)
    const tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background-color', 'white')
      .style('border', '1px solid #ccc')
      .style('padding', '10px')
      .style('border-radius', '5px')

    data.forEach((d, index) => {
      g.append('path')
        .datum(d[1])
        .attr('fill', 'none')
        .attr('stroke', (d) => {
          return color(d[0].name.split('-')[0])
        })
        .attr('stroke-width', 3)
        .attr('stroke-dasharray', (d) => {
          return d[0].name.split('-')[1] === 'occupancy' ? 'none' : '5,5'
        })
        .attr('d', line)

      const tooltip = d3
        .select('body')
        .append('div')
        .attr('class', 'tooltip')
        .style('position', 'absolute')
        .style('visibility', 'hidden')
      // Add circles to each point in the dataset
      g.selectAll(`circle-${index}`)
        .data(d[1] as dataSetType[])
        .enter()
        .append('circle')
        .attr('cx', (d) => xScale(d.date))
        .attr('cy', (d) => yScale(d.value))
        .attr('r', 5)
        .attr('fill', (d) => {
          return color(d.name.split('-')[0])
        })
        .on('mouseover', function (event, d) {
          const formattedDate = formatTime(d.date)
          const formattedValue = d.value.toFixed(2)
          tooltip
            .style('visibility', 'visible')
            .html(
              `Date: ${formattedDate}<br/>Value: ${formattedValue}<br/>Floor: ${getFloorName(
                d.name
              )}<br/>Type: ${d.name.split('-')[1]}`
            )
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 20}px`)
        })
        .on('mousemove', function (event) {
          tooltip
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 20}px`)
        })
        .on('mouseout', function () {
          tooltip.style('visibility', 'hidden')
        })
    })
  }, [data, svgWidth])

  const removeFloor = (floor, element) => {
    if (data.some((d) => d[0] === floor)) {
      const updateddataSet = data.filter((d) => d[0] !== floor)
      setData(updateddataSet)
      setSelectedType(
        updateddataSet.length > 0 ? updateddataSet.map((el) => el[0]) : ['all']
      )
    } else {
      let updated = [...data]
      updated.push(element)
      setData(updated)
      setSelectedType(updated.length > 0 ? updated.map((el) => el[0]) : ['all'])
    }
  }

  return (
    <div className=' w-full h-full'>
      {/* {floorLables.length > 1 && (
        <SegmentedControl size="md" radius="xl" data={floorLables} />
      )} */}
      <div className='flex md:flex-row flex-col gap-6 justify-between md:items-center'>
        <div className='flex gap-2 items-center'>
          {/* {data.length > 0 && (
            <>
              <p className="font-bold text-xl m-0 ">
                {data.map((d, index) => (
                  <span className="capitalize pr-1" key={d[0]}>
                    {index === data.length - 1 ? d[0] : `${d[0]} /`}
                  </span>
                ))}
                per floor
              </p>
            </>
          )} */}
          <p className='font-bold text-2xl m-0'>Occupancy per floor</p>
        </div>

        <div className='flex gap-2 self-end'>
          {dataGroups.map((d, index) => {
            const isDisabled = !data.some((item) => item[0] === d[0])
            const desc = typeDescription.find(
              (item) => item.type === d[0]
            )?.description
            const name =
              floors
                .find((floor) => floor.value === d[0].split('-')[0])
                ?.label.split('- ')[1] || d[0]
            const type = d[0].split('-')[1]

            return (
              <Tooltip
                key={d[0]}
                className='w-fit max-w-[200px]  '
                withArrow
                label={
                  <span className='text-wrap capitalize '>
                    {desc || `${name} - ${type}`}
                  </span>
                }
              >
                <div
                  onClick={() => removeFloor(d[0], d)}
                  className={`flex  gap-2 items-center cursor-pointer rounded-[100px] border py-2 px-4 border-solid border-grayText4`}
                >
                  <svg
                    width='22'
                    height='22'
                    viewBox='0 0 24 24'
                    xmlns='http://www.w3.org/2000/svg'
                    className='flex-shrink-0'
                  >
                    <circle
                      cx='12'
                      cy='12'
                      r='10'
                      fill='none'
                      stroke={
                        isDisabled ? '#BBC3CF' : color(d[0].split('-')[0])
                      }
                      strokeWidth='2'
                      strokeDasharray={
                        d[0].split('-')[1] === 'occupancy' ? '0' : '4 4'
                      }
                    />

                    <circle
                      cx='12'
                      cy='12'
                      r='6'
                      fill={isDisabled ? '#BBC3CF' : color(d[0].split('-')[0])}
                    />
                  </svg>

                  <p
                    className={`lg:text-sm text-xs ${
                      isDisabled ? 'text-[#D5DAE1]' : 'black'
                    } capitalize truncate sm:max-w-[150px] max-w-[90px]`}
                  >
                    {name} - {type}
                  </p>
                </div>
              </Tooltip>
            )
          })}
        </div>
      </div>
      <div id='container'>
        <svg ref={lineChartConatiner} />
      </div>
    </div>
  )
}

export default LineChart
